import React, { useState, useEffect, useRef } from 'react';
import WarehouseActions from '../components/warehouses/WarehouseActions';
import WarehousesTable from '../components/warehouses/WarehousesTable';
import WarehouseForm from '../components/warehouses/WarehouseForm';
import WarehouseDetails from '../components/warehouses/WarehouseDetails';
import ConfirmDialog from '../components/common/ConfirmDialog';
import useWarehouses from '../hooks/useWarehouses';
import { useTranslation } from 'react-i18next';

const PAGE_LIMIT = 20;

const WarehousesPage = () => {
  const { t } = useTranslation();
  const {
    warehouses,
    loading,
    totalCount,
    fetchWarehouses,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,
  } = useWarehouses();

  const [refreshLoading, setRefreshLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetails, setShowDetails] = useState(false);
  const [viewingWarehouseId, setViewingWarehouseId] = useState(null);
  const searchDebounceRef = useRef(null);

  // Debounce search → reset to page 1
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchTerm]);

  // Fetch on page / search change
  useEffect(() => {
    const skip = (currentPage - 1) * PAGE_LIMIT;
    fetchWarehouses({ skip, limit: PAGE_LIMIT, ...(committedSearch && { search: committedSearch }) });
  }, [fetchWarehouses, currentPage, committedSearch]);

  const handleRefresh = async () => {
    setRefreshLoading(true);
    const skip = (currentPage - 1) * PAGE_LIMIT;
    await fetchWarehouses(
      { skip, limit: PAGE_LIMIT, ...(committedSearch && { search: committedSearch }) },
      { force: true }
    );
    setRefreshLoading(false);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = (id) => {
    setEditing({ id });
    setShowForm(true);
  };

  const handleView = (id) => {
    setViewingWarehouseId(id);
    setShowDetails(true);
  };

  const handleDelete = (id) => {
    const item = warehouses.find((w) => w.id === id) || null;
    setDeleteConfirm({ id, item, title: t('warehouses.confirm.deleteSingle.title'), confirmLabel: t('common.dialogs.delete.confirm'), cancelLabel: t('common.dialogs.delete.cancel'), confirmVariant: 'danger' });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    await deleteWarehouse(deleteConfirm.id);
    setDeleteConfirm(null);
  };

  const handleFormSubmit = async (payload) => {
    try {
      const result = editing && editing.id
        ? await updateWarehouse(editing.id, payload)
        : await createWarehouse(payload);

      // Only close the form when the API actually succeeded; otherwise keep it
      // open so the form can surface the error and preserve entered data.
      if (result && result.success) {
        setShowForm(false);
        setEditing(null);
      }
      return result || { success: false, error: 'Operation failed' };
    } catch (err) {
      console.error('Form submit failed', err);
      return { success: false, error: err.message };
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_LIMIT);

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('warehouses.page.header.title')}</h1>
            <p className="mt-2 text-gray-600">{t('warehouses.page.header.subtitle')}</p>
          </div>

          <div className="flex space-x-4">
            <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">{t('warehouses.page.stats.total')}</div>
                <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WarehouseActions
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onAddWarehouse={handleAdd}
        onRefresh={handleRefresh}
        loading={loading}
        refreshLoading={refreshLoading}
      />

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
        <WarehousesTable
          warehouses={warehouses}
          loading={loading}
          onViewWarehouse={handleView}
          onEditWarehouse={handleEdit}
          onDeleteWarehouse={handleDelete}
        />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
          style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
        >
          <span className="text-sm" style={{ color: '#6b7280' }}>
            Page {currentPage} of {totalPages} — {totalCount} total warehouses
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
              className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                acc.push(p);
                return acc;
              }, [])
              .map((item) =>
                typeof item === 'string' ? (
                  <span key={item} className="px-2 text-sm" style={{ color: '#6b7280' }}>…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    disabled={loading}
                    className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: item === currentPage ? '#0066cc' : 'transparent',
                      color: item === currentPage ? '#fff' : '#374151',
                      border: item === currentPage ? 'none' : '1px solid rgba(0,0,0,0.15)',
                    }}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
              className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: '#0066cc', color: '#fff' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <WarehouseForm
          isOpen={showForm}
          warehouseId={editing?.id || null}
          onSubmit={handleFormSubmit}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {showDetails && (
        <WarehouseDetails
          isOpen={showDetails}
          warehouseId={viewingWarehouseId}
          onClose={() => {
            setShowDetails(false);
            setViewingWarehouseId(null);
          }}
        />
      )}

      {deleteConfirm && (
        <ConfirmDialog
          isOpen
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          confirmLabel={deleteConfirm.confirmLabel}
          cancelLabel={deleteConfirm.cancelLabel}
          confirmVariant={deleteConfirm.confirmVariant}
          loading={false}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default WarehousesPage;
